import { useMemo } from 'react'
import type { FC, PropsWithChildren } from 'react'

import styled from '@emotion/styled'
import type { Asset } from 'contentful'

import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronLeftIcon } from '@segment/matcha/components/Icons/Common'
import Typography from '@segment/matcha/components/Typography'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Button from '@segment/matcha/components/Button'

import CopyBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import HeroBase, * as Hero from '@segment/matcha/recipes/Hero'

import type { CustomerStory } from '@segment/api/routes/customers/queries'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

interface HeroSectionProps {
  title: string
  featuredImage?: Asset
  heroCtaText?: string
  heroCtaUrl?: string
  useCase: CustomerStory[`categoryUseCase`]
  industry: CustomerStory[`categoryIndustry`]
  location: CustomerStory[`location`]
  region: CustomerStory[`categoryRegion`]
  products: CustomerStory[`categoryProducts`]
}

const HeroSection: FC<PropsWithChildren<HeroSectionProps>> = ({
  title,
  featuredImage,
  heroCtaText,
  heroCtaUrl,
  useCase,
  industry,
  location,
  region,
  products,
}) => {
  const pageConfig = usePageConfig()

  const overviewItems = useMemo(
    () => [
      {
        title: `Use Case`,
        data: useCase,
      },
      {
        title: `Region`,
        data: region,
      },
      {
        title: `Industry`,
        data: industry,
      },
      {
        title: `Products`,
        data: products,
      },
      {
        title: `Headquarters`,
        data: location,
      },
    ],
    [useCase, industry, location, region, products]
  )

  return (
    <Wrapper>
      <StyledHeroBase variant="2Col">
        <StyledHeroCopy>
          <CopyBase>
            <StyledButtonText
              href={getAbsoluteUrl(`/customers/`, pageConfig)}
              tag="a"
              size="small"
              iconStart={ChevronLeftIcon}
              variant="mono"
            >
              Customers
            </StyledButtonText>
            <StyledCopyTextHeading tag="h1" variant="h1">
              {title}
            </StyledCopyTextHeading>
            <StyledColumnTrack columns={1} overrides={{ md: 2 }}>
              {overviewItems.map(
                (item) =>
                  item.data && (
                    <Item key={item.title}>
                      <Title variant="overline" tag="p">
                        {item.title}
                      </Title>
                      {Array.isArray(item.data) ? (
                        item.data.map((item) => (
                          <StyledDescription key={item.fields.title} variant="pMedium">
                            {item.fields.title}
                          </StyledDescription>
                        ))
                      ) : (
                        <StyledDescription variant="pMedium">{item.data}</StyledDescription>
                      )}
                    </Item>
                  )
              )}
            </StyledColumnTrack>
            {heroCtaText && heroCtaUrl && (
              <StyledButton href={heroCtaUrl} color="primary" variant="filled" size="large">
                {heroCtaText}
              </StyledButton>
            )}
          </CopyBase>
        </StyledHeroCopy>
        <StyledHeroContents>
          {featuredImage && (
            <HeroImage
              role="img"
              alt="Featured image"
              title="Featured image"
              background={featuredImage.fields.file.url}
            />
          )}
        </StyledHeroContents>
      </StyledHeroBase>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  background-color: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const Item = styled.div``

const Title = styled(Typography)`
  margin: 16px 0 8px;
  color: ${({ theme }) => theme.palette.twilio.gray[80]};
`

const Description = styled(Typography)`
  margin: 0 0 4px;
`
const StyledHeroBase = styled(HeroBase)`
  ${Hero.Wrapper} {
    grid-template-columns: 1fr;
    grid-template-areas: 'copy' 'contents';
    justify-content: center;
    align-items: center;

    ${({ theme }) => theme.breakpoints.up(`xxl`)} {
      grid-template-columns: minmax(764px, 60%) minmax(356px, 35%);
      grid-template-areas: 'copy contents';
      gap: 80px;
    }
  }
`

const StyledHeroCopy = styled(Hero.Copy)`
  max-width: unset;
  ${({ theme }) => theme.breakpoints.up(`xxl`)} {
    min-height: 720px;
  }
`

const StyledButton = styled(Button)`
  margin-top: 40px;
`

const StyledCopyTextHeading = styled(CopyText.Heading)`
  ${({ theme }) => theme.breakpoints.between(`lg`, `lg`)} {
    font-size: 40px;
    line-height: 48px;
  }
`

const StyledButtonText = styled(ButtonText)`
  ${({ theme }) => theme.typography.h5};
  font-size: 18px;
  line-height: 24px;

  ${({ theme }) => theme.breakpoints.up(`xxl`)} {
    margin-bottom: 40px;
  }
`

const StyledHeroContents = styled(Hero.Contents)`
  display: grid;
  justify-items: left;
  ${({ theme }) => theme.breakpoints.up(`xxl`)} {
    justify-items: right;
  }
`

interface HeroImageProps {
  background: string
  alt: string
}

const HeroImage = styled.div<HeroImageProps>`
  background-image: url('${({ background }) => background}');
  background-size: cover;
  background-position: center;
  display: inline-block;
  box-shadow: 0px 75px 45px -65px rgba(0, 0, 0, 0.5);
  border-radius: 10px;
  max-width: 327px;
  width: 100%;
  height: 375px;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    height: 400px;
  }

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    height: 440px;
  }

  ${({ theme }) => theme.breakpoints.up(`xxl`)} {
    max-width: 100%;
    height: 540px;
  }
`

const StyledDescription = styled(Description)`
  color: ${({ theme }) => theme.palette.twilio.gray[80]};
`

const StyledColumnTrack = styled(ColumnTrack)`
  row-gap: 16px;
`

export default HeroSection
